import { validate as validateUuid } from 'uuid';
import { ChatRef, DesignSystem, DesignSystemVersion } from 'dragonmouse-shared/interfaces';
import { VITE_FORGE_URL } from '../constants';
import {
	setSceneLoadError,
	updateSceneChatRefs,
	updateActiveSceneDesignSystem,
	updateActiveSceneLinkedDesignSystemVersion,
	updateActiveSceneLatestDesignSystemVersion,
} from '../redux/db/actions';
import store from '../redux/store';
import { PollingStream } from './PollingStream';

interface ScenePollResponse {
	chatRefs: ChatRef[];
	designSystem: DesignSystem | null;
	linkedDesignSystemVersion: DesignSystemVersion | null;
	latestDesignSystemVersion: DesignSystemVersion | null;
	timestamp: string;
}

export class ScenePollingStream extends PollingStream<ScenePollResponse> {
	constructor(activeSceneId: string) {
		// Validate UUIDs before constructing URL
		if (!validateUuid(activeSceneId)) {
			throw new Error(`Invalid scene ID: ${activeSceneId}`);
		}

		const url = VITE_FORGE_URL + `/poll/scene/${activeSceneId}`;

		const onError = (error: Error) => {
			if (error.message === 'Access denied') {
				store.dispatch(setSceneLoadError('You do not have access to this project'));
				store.dispatch(updateSceneChatRefs([]));
				store.dispatch(updateActiveSceneDesignSystem(null));
				store.dispatch(updateActiveSceneLinkedDesignSystemVersion(null));
				store.dispatch(updateActiveSceneLatestDesignSystemVersion(null));
			} else {
				console.error(error);
			}
		};

		super(url, onError, 'activeScene.chats');
	}

	extractData(response: any): ScenePollResponse[] {
		return [response];
	}

	onData(responses: ScenePollResponse[]) {
		const response = responses.length > 0 ? responses[0] : null;
		if (!response) {
			store.dispatch(updateSceneChatRefs([]));
			store.dispatch(updateActiveSceneDesignSystem(null));
			store.dispatch(updateActiveSceneLinkedDesignSystemVersion(null));
			store.dispatch(updateActiveSceneLatestDesignSystemVersion(null));
			return;
		}

		// Update chat refs
		const processedChatRefs: ChatRef[] = (response.chatRefs || []).map((ref) => ({
			...ref,
			updatedAt: new Date(ref.updatedAt),
		}));
		store.dispatch(updateSceneChatRefs(processedChatRefs));

		// Update design system
		if (response.designSystem) {
			const processedDs: DesignSystem = {
				...response.designSystem,
				createdAt: new Date(response.designSystem.createdAt),
				updatedAt: new Date(response.designSystem.updatedAt),
			};
			store.dispatch(updateActiveSceneDesignSystem(processedDs));
		} else {
			store.dispatch(updateActiveSceneDesignSystem(null));
		}

		// Update linked design system version
		if (response.linkedDesignSystemVersion) {
			const processedVersion: DesignSystemVersion = {
				...response.linkedDesignSystemVersion,
				createdAt: new Date(response.linkedDesignSystemVersion.createdAt),
			};
			store.dispatch(updateActiveSceneLinkedDesignSystemVersion(processedVersion));
		} else {
			store.dispatch(updateActiveSceneLinkedDesignSystemVersion(null));
		}

		// Update latest design system version
		if (response.latestDesignSystemVersion) {
			const processedVersion: DesignSystemVersion = {
				...response.latestDesignSystemVersion,
				createdAt: new Date(response.latestDesignSystemVersion.createdAt),
			};
			store.dispatch(updateActiveSceneLatestDesignSystemVersion(processedVersion));
		} else {
			store.dispatch(updateActiveSceneLatestDesignSystemVersion(null));
		}
	}
}
